import { AsyncPipe, CurrencyPipe } from "@angular/common";
import { Component, DestroyRef, effect, inject, input, Input, LOCALE_ID, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { AbstractControl, FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { ProductPriceData } from "@domain/product/product-price-data";
import { ProductService } from "@domain/product/product.service";
import { FloorplanAlarmDevice } from "@domain/project/floorplan/floorplan-alarm-device";
import { FloorplanFlameDetector } from "@domain/project/floorplan/floorplan-flame-detector";
import { FloorplanFlameDetectorPlaceholder } from "@domain/project/floorplan/floorplan-flame-detector-placeholder";
import { FloorplanGasWarningCenter } from "@domain/project/floorplan/floorplan-gas-warning-center";
import { FloorplanGasWarningCenterPlaceholder } from "@domain/project/floorplan/floorplan-gas-warning-center-placeholder";
import { FloorplanPlasticSign } from "@domain/project/floorplan/floorplan-plastic-sign";
import { FloorplanSignalElement } from "@domain/project/floorplan/floorplan-signal-element";
import { FloorplanSimplePlaceholder } from "@domain/project/floorplan/floorplan-simple-placeholder";
import { FloorplanTransmitter } from "@domain/project/floorplan/floorplan-transmitter";
import { FloorplanTransmitterPlaceholder } from "@domain/project/floorplan/floorplan-transmitter-placeholder";
import { FloorplanCentralStation } from "@domain/project/floorplan/monitoring/floorplan-central-station";
import { FloorplanDisplay } from "@domain/project/floorplan/monitoring/floorplan-display";
import { FloorplanKvm } from "@domain/project/floorplan/monitoring/floorplan-kvm";
import { FloorplanPatientMonitor } from "@domain/project/floorplan/monitoring/floorplan-patient-monitor";
import { FloorplanTelemetry } from "@domain/project/floorplan/monitoring/floorplan-telemetry";
import { FloorplanUtility } from "@domain/project/floorplan/monitoring/floorplan-utility";
import { PlaceholderProduct } from "@domain/project/floorplan/placeholder-product";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { FormDirective } from "@odx/angular/components/form-field";
import { CountryCurrencyMapper } from "@utils/country-currency-mapper";
import { filter, first, map, Observable, Subject, takeUntil } from "rxjs";

@Component({
  selector: "app-floorplan-custom-part-numbers",
  imports: [AsyncPipe, FormDirective, UiKitModule, ReactiveFormsModule],
  templateUrl: "./floorplan-custom-part-numbers.component.html",
  standalone: true,
})
export class FloorplanCustomPartNumbersComponent implements OnInit {
  readonly floorplanItem = input.required<
    | FloorplanSimplePlaceholder
    | FloorplanTransmitterPlaceholder
    | FloorplanGasWarningCenterPlaceholder
    | FloorplanGasWarningCenter
    | FloorplanTransmitter
    | FloorplanFlameDetector
    | FloorplanFlameDetectorPlaceholder
    | FloorplanAlarmDevice
    | FloorplanSignalElement
    | FloorplanPlasticSign
    | FloorplanPatientMonitor
    | FloorplanCentralStation
    | FloorplanTelemetry
    | FloorplanKvm
    | FloorplanDisplay
    | FloorplanUtility
  >();
  @Input() hideAddButton?: boolean = false;
  @Input() setFormInput?: boolean = true;

  private readonly formBuilder = inject(FormBuilder);
  private readonly userService = inject(UserService);
  private readonly projectService = inject(ProjectService);
  private readonly productService = inject(ProductService);
  private readonly destroyRef = inject(DestroyRef);
  private countryMapping = inject(CountryCurrencyMapper);
  private currencyPipe = inject(CurrencyPipe);
  private readonly locale = inject(LOCALE_ID);

  protected availableProductIds: string[] = [];
  protected showWarning: boolean = false;
  protected placeholderProductsForm!: FormGroup;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected readonly BusinessScope = BusinessScope;

  protected scope$ = this.userService.businessScope$;

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
    effect(() => {
      const item = this.floorplanItem();
      const cleanUp$ = new Subject<void>();
      this.initForm(item, cleanUp$);
      this.updateShowWarning();
      return () => {
        cleanUp$.next();
        cleanUp$.complete();
      };
    });
  }

  ngOnInit() {
    this.productService
      .getAllProductIds()
      .pipe(first(), takeUntilDestroyed(this.destroyRef))
      .subscribe((productIds: string[]) => {
        this.availableProductIds = productIds;
        this.updateShowWarning();
      });
  }

  protected addFormGroup(): void {
    const placeholderProducts: FormArray = this.placeholderProductsForm.get("placeholderProducts") as FormArray;
    placeholderProducts.push(this.createPlaceholderProductFormGroup());
  }

  protected getProductsControls() {
    const productsArray = this.placeholderProductsForm.get("placeholderProducts") as FormArray;
    return productsArray ? productsArray.controls : [];
  }

  private initForm(
    item:
      | FloorplanSimplePlaceholder
      | FloorplanTransmitterPlaceholder
      | FloorplanGasWarningCenterPlaceholder
      | FloorplanGasWarningCenter
      | FloorplanTransmitter
      | FloorplanFlameDetector
      | FloorplanFlameDetectorPlaceholder
      | FloorplanAlarmDevice
      | FloorplanSignalElement
      | FloorplanPlasticSign
      | FloorplanPatientMonitor
      | FloorplanCentralStation
      | FloorplanTelemetry
      | FloorplanKvm
      | FloorplanDisplay
      | FloorplanUtility,
    destroy$: Subject<void>,
  ) {
    const placeholderProductsFormArray = this.formBuilder.array(
      item.products.map((product: PlaceholderProduct) =>
        this.formBuilder.group({
          name: [this.setFormInput ? product.name : undefined],
          id: [this.setFormInput ? product.id : undefined],
          price: [this.setFormInput ? product.price?.price : undefined],
        }),
      ),
    );

    this.placeholderProductsForm = this.formBuilder.group({
      placeholderProducts: placeholderProductsFormArray,
    });

    if (!placeholderProductsFormArray.length) {
      this.addFormGroup();
    }

    this.placeholderProductsForm.valueChanges.pipe(takeUntil(destroy$), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.placeholderProductsForm.valid || this.placeholderProductsForm.pristine) return;
      this.update();
    });
  }

  private parsePrice(value: unknown): number | undefined {
    if (typeof value === "number") {
      return Number.isFinite(value) ? value : undefined;
    }
    if (typeof value !== "string" || value.trim() === "") {
      return undefined;
    }

    const decimalSeparator =
      new Intl.NumberFormat(this.locale).formatToParts(1.1).find((part) => part.type === "decimal")?.value ?? ".";
    const groupSeparator = decimalSeparator === "," ? "." : ",";

    const normalized = value
      .trim()
      .replace(/[^\d,.-]/g, "")
      .split(groupSeparator)
      .join("")
      .replace(decimalSeparator, ".");

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  protected get currencyCode(): string {
    return this.countryMapping.getCurrency(this.productService.getFilterCountry());
  }

  protected formatPriceValue(value: unknown): string {
    if (value === null || value === undefined || value === "") {
      return "";
    }
    return this.currencyPipe.transform(value as number, this.currencyCode, "symbol", "1.2-2", this.locale) ?? "";
  }

  protected processPriceValue(event: Event, control: AbstractControl) {
    const input = event.target as HTMLInputElement;
    const value = this.parsePrice(input.value);

    control.markAsDirty();
    control.get("price")?.setValue(value ?? null);
    input.value = this.formatPriceValue(value);
  }

  private createPlaceholderProductFormGroup(): FormGroup {
    return this.formBuilder.group({
      name: [null],
      id: [null],
      price: [null],
    });
  }

  private update() {
    const formArray = this.placeholderProductsForm.get("placeholderProducts") as FormArray;
    if (!formArray) {
      return;
    }
    const currencyCode = this.countryMapping.getCurrency(this.productService.getFilterCountry());
    this.floorplanItem().products = formArray.controls
      .map((control) => {
        const formGroup = control as FormGroup;
        const name = formGroup.get("name")?.value?.trim() || "";
        const productNumber = formGroup.get("id")?.value?.trim() || "";
        const cost = this.parsePrice(formGroup.get("price")?.value);
        const price = cost !== undefined ? new ProductPriceData("Custom-Type", cost, currencyCode) : undefined;
        return name || productNumber || price ? new PlaceholderProduct(name, productNumber, "", price) : undefined;
      })
      .filter((placeholderProduct): placeholderProduct is PlaceholderProduct => placeholderProduct !== undefined);

    this.updateShowWarning();
  }

  private updateShowWarning() {
    this.showWarning =
      this.floorplanItem().products.length > 0 &&
      this.floorplanItem().products.every((product: PlaceholderProduct) => this.availableProductIds.includes(product.id));
  }
}
